import { z } from "zod";
import { MAX_SELECTION_CHARS } from "@racler/shared";
import { projectIdSchema, relativePathSchema } from "./schemas.ts";

/**
 * Context items for AI requests (milestone M5.1-A).
 * Structured, provider-neutral references to project assets.
 * Server stores only safe metadata; file contents are read/snapshotted by the Agent.
 */

export const projectFileContextItemSchema = z.strictObject({
  type: z.literal("project_file"),
  projectId: projectIdSchema,
  path: relativePathSchema,
});

export const projectFolderContextItemSchema = z.strictObject({
  type: z.literal("project_folder"),
  projectId: projectIdSchema,
  path: relativePathSchema,
});

export const editorSelectionContextItemSchema = z
  .strictObject({
    type: z.literal("editor_selection"),
    projectId: projectIdSchema,
    path: relativePathSchema,
    startLine: z.number().int().positive(),
    startColumn: z.number().int().positive(),
    endLine: z.number().int().positive(),
    endColumn: z.number().int().positive(),
    selectedText: z.string().max(MAX_SELECTION_CHARS).optional(),
  })
  .refine(
    (s) => s.startLine < s.endLine || (s.startLine === s.endLine && s.startColumn <= s.endColumn),
    { message: "start line/column must be before or equal to end line/column" },
  );

export const attachmentKindSchema = z.enum(["text", "image", "pdf"]);
export type AttachmentKind = z.infer<typeof attachmentKindSchema>;

export const attachmentContextItemSchema = z.strictObject({
  type: z.literal("attachment"),
  projectId: projectIdSchema,
  attachmentId: z.uuid(),
  name: z.string().min(1).max(255),
  mediaType: z.string().min(1).max(255),
  size: z.number().int().nonnegative(),
  kind: attachmentKindSchema,
});

export const contextItemSchema = z.discriminatedUnion("type", [
  projectFileContextItemSchema,
  projectFolderContextItemSchema,
  editorSelectionContextItemSchema,
  attachmentContextItemSchema,
]);

export type ProjectFileContextItem = z.infer<typeof projectFileContextItemSchema>;
export type ProjectFolderContextItem = z.infer<typeof projectFolderContextItemSchema>;
export type EditorSelectionContextItem = z.infer<typeof editorSelectionContextItemSchema>;
export type AttachmentContextItem = z.infer<typeof attachmentContextItemSchema>;
export type ContextItem = z.infer<typeof contextItemSchema>;

/**
 * Validates that every ContextItem belongs to the expected project.
 * Throws or returns false if any item has a mismatched projectId.
 */
export function validateContextItemsProject(items: ContextItem[], expectedProjectId: string): boolean {
  return items.every((item) => item.projectId === expectedProjectId);
}
